import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import ConsoleDrawer from '../components/ConsoleDrawer';
import ModuleMetaModal from '../components/ModuleMetaModal';
import TopicEditModal from '../components/TopicEditModal';
import ModulePill from '../components/ModulePill';
import ModuleCard from '../components/ModuleCard';
import {
  getModules, createInvite, createModule, deleteModule, deleteTopic, api,
} from '../lib/apiClient';
import BackupMenu from '../components/BackupMenu';

const DeveloperDashboard = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // editing state
  const [editingModule, setEditingModule] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null); // { moduleId, topic }
  const [addingTopicTo, setAddingTopicTo] = useState(null); // moduleId or null
  const [creatingModule, setCreatingModule] = useState(false);

  // Invite
  const [inviteRole, setInviteRole] = useState('student');
  const [inviteEmail, setInviteEmail] = useState('');
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const inviteInputRef = React.useRef(null);

  // Bulk LearnEx bootstrap — generate quizzes for every topic in a module.
  const [bootstrappingId, setBootstrappingId] = useState(null);
  const [bootstrapReport, setBootstrapReport] = useState(null);
  const [renumberingId, setRenumberingId] = useState(null);

  const handleModuleAudit = async () => {
    try {
      const r = await api.get('/api/admin/module-integrity');
      const s = r.data.summary;
      const lines = [
        `🩺 Module Integrity Audit`,
        ``,
        `MODULES:                ${s.modules}`,
        `TOTAL TOPICS:           ${s.total_topics}`,
        `TOPICS WITHOUT QUIZ:    ${s.topics_without_quiz}`,
        `MISMATCHED ROWS:        ${s.mismatched_rows}   (module_id ≠ topic's home — would mean a move bug)`,
        `ORPHANED ROWS:          ${s.orphaned_rows}   (topic_id no longer exists anywhere)`,
        ``,
      ];
      if (r.data.topics_without_quiz.length) {
        lines.push('TOPICS NEEDING A QUIZ:');
        r.data.topics_without_quiz.slice(0, 20).forEach((t) =>
          lines.push(`  • ${t.title || t.topic_id} (in ${t.module_id})`));
        if (r.data.topics_without_quiz.length > 20)
          lines.push(`  …and ${r.data.topics_without_quiz.length - 20} more`);
        lines.push('');
      }
      if (r.data.mismatched.length) {
        lines.push('⚠ MISMATCHED ROWS (each row counted):');
        r.data.mismatched.slice(0, 12).forEach((m) =>
          lines.push(`  • ${m.collection}: ${m.rows} row(s), topic ${m.topic_id.slice(0,18)}…  is ${m.actual_module_id} but should be ${m.expected_module_id}`));
        lines.push('');
      }
      if (r.data.orphans.length) {
        lines.push('⚠ ORPHANED ROWS (topic deleted but rows left behind):');
        r.data.orphans.slice(0, 12).forEach((o) =>
          lines.push(`  • ${o.collection}: ${o.rows} row(s) for topic ${o.topic_id.slice(0,18)}…`));
        lines.push('');
      }
      if (s.mismatched_rows === 0 && s.orphaned_rows === 0 && s.topics_without_quiz === 0) {
        lines.push('✓ All modules pass — no drift, no missing quizzes, no orphans.');
      }
      // eslint-disable-next-line no-alert
      alert(lines.join('\n'));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Audit failed.');
    }
  };

  const handleMoveTopic = async (srcMod, topic, destModuleId) => {
    const destMod = modules.find((x) => x.id === destModuleId);
    if (!destMod) return;
    if (!window.confirm(
      `Move "${topic.title || 'Untitled'}" from ${srcMod.title} to ${destMod.title}?\n\n` +
      'All linked content stays attached: presentation, video, summary, ' +
      'LearnEx quiz, sub-topics, every student attempt and gradebook entry. ' +
      'Both modules will be auto-renumbered after the move.'
    )) return;
    try {
      const r = await api.post(
        `/api/modules/${srcMod.id}/topics/${topic.id}:move`,
        { dest_module_id: destModuleId });
      // eslint-disable-next-line no-alert
      alert(
        `✓ Moved.\n\n` +
        `${srcMod.title} now has: ${r.data.from_module.topics.map((t) => `T${t.order} ${t.title}`).join(', ') || '(empty)'}\n\n` +
        `${destMod.title} now has: ${r.data.to_module.topics.map((t) => `T${t.order} ${t.title}`).join(', ')}\n\n` +
        (Object.keys(r.data.rewired_collections || {}).length
          ? `Re-pointed rows: ${Object.entries(r.data.rewired_collections).map(([k,v]) => `${k}=${v}`).join(', ')}`
          : 'No linked rows needed rewiring.')
      );
      refresh();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Move failed.');
    }
  };

  const handleRenumberTopics = async (mod) => {
    if (!window.confirm(
      `Renumber topics in "${mod.title}" to 1..N?\n\n` +
      'Use this if numbering shows gaps (e.g. "Topic 2, Topic 3, …" after a delete). ' +
      'Idempotent — safe to fire as many times as you want.'
    )) return;
    setRenumberingId(mod.id);
    try {
      const r = await api.post(`/api/modules/${mod.id}/topics:renumber`);
      // eslint-disable-next-line no-alert
      alert(
        `Renumbered ${r.data.count} topic(s):\n\n` +
        r.data.topics.map((t) => `  Topic ${t.order} — ${t.title}`).join('\n')
      );
      refresh();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Renumber failed.');
    } finally { setRenumberingId(null); }
  };

  const handleBulkBootstrap = async (moduleId) => {
    if (!window.confirm(
      'Bootstrap LearnEx for every topic in this module?\n\n' +
      '• Each topic that has a Presentation or Video AND no quiz yet → ' +
      'a new 5-question case-analysis quiz is generated (~30s per topic).\n' +
      '• Each topic that already has a quiz → its correct-answer positions ' +
      'are rebalanced across A/B/C/D (instant).\n\n' +
      'Continue?'
    )) return;
    setBootstrappingId(moduleId);
    setBootstrapReport(null);
    try {
      const r = await api.post(`/api/modules/${moduleId}/learnex/bulk-bootstrap`);
      setBootstrapReport(r.data);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Bulk bootstrap failed.');
    } finally { setBootstrappingId(null); }
  };


  const refresh = () => {
    setLoading(true);
    getModules().then(setModules).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setInviteError(''); setGeneratedInvite(null); setCreatingInvite(true);
    try {
      const data = await createInvite(inviteRole, inviteEmail);
      setGeneratedInvite(data);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const inviteDisplayUrl = generatedInvite
    ? generatedInvite.invite_url.replace(/https?:\/\/[^/]+/, window.location.origin) : '';

  const copyInviteLink = async () => {
    if (!inviteDisplayUrl) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(inviteDisplayUrl);
        setCopyStatus('Copied!'); setTimeout(() => setCopyStatus(''), 2000); return;
      }
    } catch (_) {}
    try {
      inviteInputRef.current?.focus();
      inviteInputRef.current?.select();
      if (document.execCommand('copy')) {
        setCopyStatus('Copied!'); setTimeout(() => setCopyStatus(''), 2000); return;
      }
    } catch (_) {}
    setCopyStatus('Press Ctrl/Cmd+C to copy');
    setTimeout(() => setCopyStatus(''), 3000);
  };

  const handleAddModule = async () => {
    if (creatingModule) return;
    setCreatingModule(true);
    try {
      const nextOrder = modules.reduce((mx, m) => Math.max(mx, m.order || 0), 0) + 1;
      await createModule({ title: `Module ${nextOrder}`, order: nextOrder, status: 'draft' });
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not create module');
    } finally {
      setCreatingModule(false);
    }
  };

  const handleDeleteModule = async (m) => {
    if (!window.confirm(`Delete "${m.title}" and all its topics? This cannot be undone.`)) return;
    try { await deleteModule(m.id); refresh(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleDeleteTopic = async (moduleId, topic) => {
    if (!window.confirm(`Delete topic "${topic.title}"?`)) return;
    try { await deleteTopic(moduleId, topic.id); refresh(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="min-h-screen" data-testid="developer-dashboard">
      <Navbar />
      <ConsoleDrawer role="developer" actions={{ 'module-audit': handleModuleAudit }}>
      <main className="max-w-7xl mx-auto px-6 py-10 pl-16">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Developer Console</h1>
            <p className="text-gray-500 mt-1">Manage modules, topics, and invitations.</p>
          </div>
          <BackupMenu />
        </div>

        {/* Nav has moved into the left ConsoleDrawer (drawer + AI-Tutor pane + resize knob).
            The action-group pill rows previously rendered here are now delivered via the drawer.
            Retained: BackupMenu (top-right), Invite section, and Modules editor below. */}

        {/* Invite section */}
        <section className="card p-6 mb-10" data-testid="invite-section">
          <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--unthsc-blue)' }}>Create Invitation</h2>
          <p className="text-sm text-gray-500 mb-4">Generate a one-time link for a new user.</p>
          <form onSubmit={handleCreateInvite} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Role</label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="input-field"
                      data-testid="invite-role-select">
                <option value="student">Student</option>
                <option value="teacher">Teacher (Professor/TA)</option>
                <option value="institution_admin">Institution Admin</option>
                <option value="developer">Developer</option>
              </select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email (optional)</label>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                     placeholder="user@example.com" className="input-field" data-testid="invite-email-input" />
            </div>
            <button type="submit" disabled={creatingInvite} className="btn-primary" data-testid="invite-create-btn">
              {creatingInvite ? 'Creating…' : 'Generate Link'}
            </button>
          </form>
          {inviteError && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{inviteError}</div>}
          {generatedInvite && (
            <div className="mt-4 p-4 rounded-lg" style={{ background: 'var(--unthsc-green-light)' }}>
              <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--unthsc-green-dark)' }}>
                Invite for {generatedInvite.role}
              </div>
              <div className="flex gap-2 items-center">
                <input ref={inviteInputRef} readOnly value={inviteDisplayUrl}
                       onFocus={(e) => e.target.select()} className="input-field flex-1" />
                <button onClick={copyInviteLink} className="btn-secondary text-sm" data-testid="invite-copy-btn">
                  {copyStatus || 'Copy'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Modules + nested Topics */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Modules</h2>
              <p className="text-sm text-gray-500">Each module contains one or more topics. Add new modules and topics one at a time.</p>
            </div>
            <button onClick={handleAddModule} disabled={creatingModule}
                    className="btn-primary text-sm" data-testid="add-module-btn">
              {creatingModule ? 'Adding…' : '+ Add Module'}
            </button>
          </div>

          {loading ? (
            <div className="text-gray-500">Loading…</div>
          ) : modules.length === 0 ? (
            <div className="card p-8 text-center text-gray-500" data-testid="no-modules-state">
              No modules yet. Click <span className="font-semibold" style={{ color: 'var(--unthsc-green)' }}>+ Add Module</span> to create the first one.
            </div>
          ) : (
            <div className="space-y-4" data-testid="developer-modules-list">
              {modules.map((m) => (
                <div key={m.id} className="card p-5" data-testid={`module-row-${m.order}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="mb-2">
                        <ModulePill number={m.order} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 truncate" data-testid={`module-title-${m.order}`}>
                        {m.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={m.status === 'published' ? 'badge-published' : 'badge-draft'}>
                        {m.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      <button
                        onClick={() => handleBulkBootstrap(m.id)}
                        disabled={bootstrappingId === m.id}
                        className="btn-outline text-xs font-bold disabled:opacity-50"
                        style={{ background: 'var(--gr-gold, #FCD359)', color: 'var(--gr-navy, #1A2B4C)' }}
                        data-testid={`module-bootstrap-learnex-${m.order}`}
                        title="Generate LearnEx quizzes for every topic in this module that has Presentation/Video and no quiz yet. Re-balances existing quizzes.">
                        {bootstrappingId === m.id ? '⏳ Bootstrapping…' : '🎯 Bootstrap LearnEx'}
                      </button>
                      <button
                        onClick={() => handleRenumberTopics(m)}
                        disabled={renumberingId === m.id}
                        className="btn-outline text-xs disabled:opacity-50"
                        data-testid={`module-renumber-${m.order}`}
                        title="Compact the topic order to 1..N. Use after deleting a topic — closes the gap so the sidebar no longer shows Topic 2, Topic 3, …">
                        {renumberingId === m.id ? '⏳ …' : '🔢 Renumber topics'}
                      </button>
                      <button onClick={() => setEditingModule(m)} className="btn-secondary text-xs py-1 px-2"
                              data-testid={`module-edit-${m.order}`}>Settings</button>
                      <button onClick={() => handleDeleteModule(m)}
                              className="text-sm text-red-600 hover:text-red-700 font-bold px-1.5"
                              data-testid={`module-delete-${m.order}`} title="Delete module">✕</button>
                    </div>
                  </div>

                  {/* Bulk bootstrap report — appears only after a run */}
                  {bootstrapReport && bootstrapReport.module_id === m.id && (
                    <div className="mb-3 rounded border p-3"
                         style={{
                           borderColor: 'var(--gr-gold-dark, #E0B73D)',
                           background: 'rgba(252,211,89,0.08)',
                         }}
                         data-testid={`module-bootstrap-report-${m.order}`}>
                      <div className="text-xs font-bold mb-2" style={{ color: 'var(--gr-navy, #1A2B4C)' }}>
                        🎯 LearnEx bootstrap report — {bootstrapReport.count} topic(s) processed
                      </div>
                      <ul className="text-xs space-y-1">
                        {bootstrapReport.topics.map((t) => (
                          <li key={t.topic_id} className="flex gap-2 items-baseline">
                            <span className="font-bold w-24 shrink-0">
                              {t.action === 'generated' && <span className="text-green-700">✓ Generated</span>}
                              {t.action === 'rebalanced' && <span className="text-amber-700">🔀 Rebalanced</span>}
                              {t.action === 'skipped' && <span className="text-gray-500">— Skipped</span>}
                              {t.action === 'failed' && <span className="text-red-700">✗ Failed</span>}
                            </span>
                            <span className="truncate">{t.title || t.topic_id}</span>
                            {t.correct_positions && (
                              <span className="text-gray-500 ml-auto font-mono tracking-wider">
                                {t.correct_positions
                                  .map((p) => String.fromCharCode(65 + p))
                                  .join(' · ')}
                              </span>
                            )}
                            {t.reason && (
                              <span className="text-gray-500 italic ml-auto">{t.reason}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Topics */}
                  <div className="border-t border-gray-100 pt-3">
                    {(m.topics || []).length === 0 ? (
                      <div className="text-sm text-gray-500 italic mb-2">No topics yet.</div>
                    ) : (
                      <ul className="space-y-2 mb-3">
                        {m.topics.map((t) => (
                          <li key={t.id} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50"
                              data-testid={`topic-row-${t.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                Topic {t.order}: {t.title || <span className="italic text-gray-400">untitled</span>}
                              </div>
                              <div className="text-[11px] text-gray-500 flex flex-wrap gap-2 mt-0.5">
                                {t.summary && <span>📝 summary</span>}
                                {t.presentation_url && <span>📄 PDF</span>}
                                {t.video_url && <span>🎬 video</span>}
                                {(t.assignment_text || t.assignment_url) && <span>✏ assignment</span>}
                                {(t.lab_text || t.lab_url) && <span>🧪 lab</span>}
                                {(t.discussion_text || t.discussion_url) && <span>💬 discussion</span>}
                                {(t.references_text || t.references_url) && <span>📚 references</span>}
                                {t.quiz_url && <span>❓ quiz</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <select
                                value=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleMoveTopic(m, t, e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                                className="text-xs border border-gray-300 rounded px-1.5 py-1
                                           bg-white text-gray-700 cursor-pointer max-w-[140px]"
                                data-testid={`topic-move-select-${t.id}`}
                                title="Move this topic (with all its content, quiz, attempts, gradebook entries) to another module.">
                                <option value="">Move to…</option>
                                {modules.filter((x) => x.id !== m.id).map((dest) => (
                                  <option key={dest.id} value={dest.id}>
                                    M{dest.order}: {(dest.title || '').slice(0, 18)}
                                  </option>
                                ))}
                              </select>
                              <button onClick={() => setEditingTopic({ moduleId: m.id, topic: t })}
                                      className="btn-secondary text-xs py-1 px-2"
                                      data-testid={`topic-edit-${t.id}`}>Edit</button>
                              <button onClick={() => handleDeleteTopic(m.id, t)}
                                      className="text-xs text-red-600 hover:text-red-700 font-bold px-1.5"
                                      data-testid={`topic-delete-${t.id}`} title="Delete topic">✕</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button onClick={() => setAddingTopicTo(m.id)}
                            className="btn-outline text-xs"
                            data-testid={`add-topic-${m.order}`}>
                      + Add Topic
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      </ConsoleDrawer>

      {editingModule && (
        <ModuleMetaModal module={editingModule}
                         onClose={() => setEditingModule(null)}
                         onSaved={() => { setEditingModule(null); refresh(); }} />
      )}
      {editingTopic && (
        <TopicEditModal moduleId={editingTopic.moduleId} topic={editingTopic.topic}
                        onClose={() => setEditingTopic(null)}
                        onSaved={() => { setEditingTopic(null); refresh(); }} />
      )}
      {addingTopicTo && (
        <TopicEditModal moduleId={addingTopicTo} topic={null}
                        onClose={() => setAddingTopicTo(null)}
                        onSaved={() => { setAddingTopicTo(null); refresh(); }} />
      )}
    </div>
  );
};

export default DeveloperDashboard;
